import { useEffect, useState } from 'react';

import { ParsedUrlQueryInput } from 'querystring';

import Link, { LinkProps } from 'next/link';
import Router, { useRouter } from 'next/router';

import { Button, CardGrid, EmptyState, Flexbox, Text } from '@morressier/ts';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { eachDayOfInterval, isValid, startOfDay } from 'date-fns';
import capitalize from 'lodash/capitalize';
import { BiCalendarX } from 'react-icons/bi';
import { FaFilter } from 'react-icons/fa';

import {
  SessionQueryParamsV3,
  SessionTypeFilters,
  TimeFilterStrings,
  TimeFilterTypes,
} from 'api/session';
import { parseQuery } from 'api/utils';
import { BlockedComponentType } from 'components/EventStandardContainer';
import { Drawer } from 'components/FilterDrawer/style';
import { FilterArgs, FilterSidebar } from 'components/FilterSidebar';
import { ClearFilters, CustomButton, FilterBoxContainer } from 'components/FilterSidebar/styles';
import {
  cleanAllFilters,
  getDayString,
  getTimeFilter,
  getTypeFilter,
} from 'components/FilterSidebar/utils';
import Paging from 'components/Paging';
import { GridSkeleton } from 'components/Skeleton/Grid';
import { PageProps } from 'pages/event/[eventId]/sessions';
import { useAuthContext, useEventContext } from 'store';
import { getEventDays } from 'utils/dates';
import { logEvent } from 'utils/eventTracking';

import * as ContainerStyles from '../styles';
import { useEventSessionsV3, useSessionsBookmarks } from './hooks';
import { SessionsListItem } from './SessionsListItem';
import * as Styles from './styles';
import { groupSessionsByDay } from './util';

const getTypeFilterText = (type?: SessionTypeFilters) => {
  if (type === SessionTypeFilters.Networking || type === SessionTypeFilters.Presentations) {
    return capitalize(type.replace('ns', 'n'));
  }
  return '';
};

const getTimeFilterText = ({ time }: { time?: TimeFilterTypes }) => {
  if (time && isValid(new Date(time))) {
    const date = new Date(time);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const formattedDate = `${month} ${day}`;

    return `on ${formattedDate}` as string;
  }

  switch (time) {
    case TimeFilterStrings.HappeningNow:
      return 'Happening now';

    case TimeFilterStrings.Past:
      return 'Past';

    case TimeFilterStrings.Upcoming:
      return 'Upcoming';

    default:
      return '';
  }
};

const getAllEventDays = (start: Date, end: Date) => {
  const allEventDays = getEventDays(eachDayOfInterval({ start, end }));
  allEventDays.shift();
  return allEventDays;
};

export const SessionsListContainer: React.FC<PageProps['data']> & BlockedComponentType = props => {
  const { query: _query, push, pathname } = useRouter();
  const { event } = props;
  const isWebinar = event.is_webinar;

  let eventDays;

  if (isWebinar) {
    eventDays = getEventDays(props.dates);
  } else if (['Onsite', 'Hybrid', 'Physical'].includes(event.type)) {
    eventDays = getAllEventDays(
      // Convert event start/end dates to UTC then to Event timezone
      new Date(event.start_date),
      new Date(event.end_date),
    );
  } else {
    eventDays = getAllEventDays(new Date(event.start_date), new Date(event.end_date));
  }

  const query = _query as SessionQueryParamsV3<SessionTypeFilters>;
  const { data, loading, removeSession } = useEventSessionsV3(query);
  const { state } = useEventContext();

  const { eventId, page_size, page, ..._restQuery } = query;

  // we don't want to treat with agenda as a query filter for UI purposes
  const { with_agenda: _with_agenda, ...restQuery } = _restQuery;
  const hasRestQuery = restQuery && !!Object.keys(restQuery).length;

  const { q: _searchQuery = '', ...restQueryWithoutSearch } = restQuery;
  const appliedFiltersCount = Object.keys(
    cleanAllFilters(parseQuery(restQueryWithoutSearch)),
  ).length;

  const total = data?.total || 0;
  const totalFilterResults = data?.filter_total;

  const timeFilter = getTimeFilter(query.time);
  const typeFilter = getTypeFilter(query.type);

  const theme = useTheme();
  const isBelowXs = !useMediaQuery(theme.breakpoints.up('xs'));
  const isBelowLg = useMediaQuery(theme.breakpoints.down('md'));

  const onlyFeatured = query.featured === 'true';
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(_searchQuery);
  const onSearchChange = newQuery => setSearchQuery(newQuery);

  const timeFilterText = getTimeFilterText({
    time: timeFilter,
  }).replace('now', 'Now');
  const isNotADateOrHappeningNow = [
    TimeFilterStrings.Past,
    TimeFilterStrings.Upcoming,
    TimeFilterStrings.ShowAll,
  ].includes(timeFilterText.toLowerCase() as TimeFilterStrings);
  const title = `There are no ${isNotADateOrHappeningNow ? timeFilterText : ''} ${
    onlyFeatured ? 'Featured' : ''
  } ${getTypeFilterText(typeFilter)} Sessions${
    !isNotADateOrHappeningNow ? `${timeFilterText ? ` ${timeFilterText}` : ''}.` : '.'
  }`;

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const pageRouteBuilder = (pageNumber: number): LinkProps['href'] => ({
    pathname,
    query: { ...(parseQuery(query) as ParsedUrlQueryInput), page: pageNumber },
  });

  const clearFilters = () => {
    setSearchQuery('');
    // we need to keep with_agenda when clearing filters
    push({ pathname, query: { eventId, with_agenda: query.with_agenda } }, undefined, {
      shallow: true,
    });
  };

  const { state: authState } = useAuthContext();
  const userId = authState?.profile?.id;

  const getSessionIds = () => {
    if (data?.sessions) {
      const _sessions: MorressierSession[] = [];
      Object.values(data.sessions).forEach(sessionList => _sessions.push(...sessionList));
      return _sessions.map(s => s._id).join();
    }

    return undefined;
  };

  const sessionIds = getSessionIds();
  const result = useSessionsBookmarks(true, eventId as string, sessionIds, userId);

  const handleFiltersChange = ({
    q = query?.q,
    time = query?.time,
    type = query?.type,
    featured = onlyFeatured,
    with_agenda = query?.with_agenda,
  }: FilterArgs<SessionTypeFilters>) => {
    const { page: _p, page_size: _ps, search, ...rest } = query;

    return {
      pathname,
      query: cleanAllFilters(
        parseQuery({
          ...rest,
          eventId,
          time,
          type,
          featured: featured || 'all',
          q,
          with_agenda,
        }),
      ) as ParsedUrlQueryInput,
    };
  };

  const getClearText = () => {
    const hasFilters = !!Object.keys(cleanAllFilters(parseQuery(restQueryWithoutSearch))).length;

    switch (true) {
      case !!_searchQuery && hasFilters: {
        return 'Clear all';
      }
      case !!_searchQuery: {
        return 'Clear search';
      }

      case hasFilters: {
        return 'Clear filters';
      }

      default: {
        return 'Clear all';
      }
    }
  };

  const [hasRedirectedToCurrentDay, setHasRedirectedToCurrentDay] = useState(false);

  /**
   * We only want to run this hook once onMount and only when there are no predefined query filters
   * The goal is to redirect to the present day's time filter if the present day matches one of the session days
   */
  useEffect(() => {
    if (hasRedirectedToCurrentDay) return;

    const time = props.dates?.find(date => getDayString(date) === getDayString(new Date()));
    const { eventId: evtId, ...RouterQuery } = Router.query;
    const hasRouterQuery = !!Object.keys(RouterQuery).length;

    if (time && !hasRouterQuery) {
      setHasRedirectedToCurrentDay(true);
      Router.push({
        pathname: Router.pathname,
        query: { eventId: evtId, time: startOfDay(new Date(time)).toISOString() },
      });
    }
  }, [eventId, hasRedirectedToCurrentDay, props.dates]);

  useEffect(() => {
    logEvent('STANDALONE_FILTERS_APPLIED', {
      isSessions: true, // Temporal to distiguish before adding context with page_url
      searchTerm: query.q,
      filters: query,
    });
  }, [query]);

  const filters = (
    <>
      <Link shallow href={handleFiltersChange({ type: SessionTypeFilters.ShowAll })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === SessionTypeFilters.ShowAll} />
          <Text>All Types</Text>
        </Flexbox>
      </Link>

      <Link shallow href={handleFiltersChange({ type: SessionTypeFilters.Presentations })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === SessionTypeFilters.Presentations} />
          <Text>Presentations</Text>
        </Flexbox>
      </Link>

      <Link shallow href={handleFiltersChange({ type: SessionTypeFilters.Networking })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === SessionTypeFilters.Networking} />
          <Text>Networking</Text>
        </Flexbox>
      </Link>
    </>
  );

  const groupedSessions = groupSessionsByDay(data?.sessions);

  return (
    <ContainerStyles.PageContainer withTopMargin>
      <Grid container spacing={4}>
        {isBelowLg ? (
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            BackdropProps={{ style: { opacity: 0.5, backgroundColor: '#424D5D' } }}
            onBackdropClick={handleDrawerToggle}
          >
            <FilterSidebar
              event={event}
              isBelowLg={isBelowLg}
              dates={eventDays}
              clearFilters={clearFilters}
              onClose={handleDrawerToggle}
              totalFilterResults={totalFilterResults}
              handleFiltersChange={handleFiltersChange}
              filters={filters}
              showTags
            />
          </Drawer>
        ) : (
          <Grid lg={3} item>
            <FilterSidebar
              event={event}
              dates={eventDays}
              onClose={handleDrawerToggle}
              totalFilterResults={totalFilterResults}
              handleFiltersChange={handleFiltersChange}
              filters={filters}
              showTags
            />
          </Grid>
        )}

        <Grid xs={12} lg={9} item>
          <ContainerStyles.PageTitle>Sessions</ContainerStyles.PageTitle>

          <ContainerStyles.ThemeOverides color={{ primaryBrand: '#424d5d' }}>
            <Styles.StyledSearchBar
              placeholder="Search titles"
              onSubmit={(q?: string) => push(handleFiltersChange({ q }))}
              logEvent={logEvent}
              style={{ marginBottom: '1rem' }}
              query={searchQuery}
              onChange={onSearchChange}
              controlled
            />
          </ContainerStyles.ThemeOverides>

          {isBelowLg && (
            <FilterBoxContainer>
              <Flexbox flexWrap="wrap">
                {isBelowXs ? (
                  <CustomButton
                    onClick={() => handleDrawerToggle()}
                    selected={timeFilter === TimeFilterStrings.Past}
                    round
                    fullWidth
                  >
                    <FaFilter
                      color="#424D5D"
                      style={{ marginRight: '10px', marginBottom: '-2px' }}
                    />
                    Filters {!!appliedFiltersCount && `(${appliedFiltersCount})`}
                  </CustomButton>
                ) : (
                  <>
                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.ShowAll })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.ShowAll} round>
                        Show All
                      </CustomButton>
                    </Link>

                    <Link
                      shallow
                      href={handleFiltersChange({ time: TimeFilterStrings.HappeningNow })}
                    >
                      <CustomButton selected={timeFilter === TimeFilterStrings.HappeningNow} round>
                        Happening Now
                      </CustomButton>
                    </Link>

                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.Upcoming })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.Upcoming} round>
                        Upcoming
                      </CustomButton>
                    </Link>

                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.Past })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.Past} round>
                        Past Sessions
                      </CustomButton>
                    </Link>

                    <CustomButton onClick={() => handleDrawerToggle()} round>
                      <FaFilter
                        color="#424D5D"
                        style={{ marginRight: '10px', marginBottom: '-2px' }}
                      />
                      More filters {!!appliedFiltersCount && `(${appliedFiltersCount})`}
                    </CustomButton>
                  </>
                )}
              </Flexbox>
            </FilterBoxContainer>
          )}

          {loading ? (
            <GridSkeleton />
          ) : (
            <>
              <div className="mb4">
                {hasRestQuery ? (
                  <Text color="grey">
                    {Object.keys(data?.sessions || {}).length === 0 ? (
                      <>
                        {totalFilterResults} of {total} {total === 1 ? 'Session' : 'Sessions'}.
                      </>
                    ) : (
                      <>
                        {totalFilterResults} of {total} {total === 1 ? 'Session' : 'Sessions'}. To
                        see the{' '}
                        {String(query?.with_agenda) === 'true' ? 'My Agenda' : 'full Program'}{' '}
                        <ClearFilters onClick={() => clearFilters()}>{getClearText()}</ClearFilters>
                      </>
                    )}
                  </Text>
                ) : (
                  <Text color="grey">
                    {total} {total === 1 ? 'Session' : 'Sessions'}
                  </Text>
                )}
              </div>
              {data?.sessions &&
                Object.keys(groupedSessions).map(sessionDay => {
                  const dayString = sessionDay;
                  const sessions = groupedSessions[sessionDay];

                  return (
                    <div key={sessionDay} className="mb4 gap-vert-2">
                      {!isValid(timeFilter) && (
                        <div className="mb2">
                          <Text size="body1_new" fontWeight="bold">
                            {dayString}
                          </Text>
                        </div>
                      )}
                      <CardGrid distribution="list">
                        {sessions?.length > 0 &&
                          sessions.map(session => (
                            <SessionsListItem
                              key={session._id}
                              session={session}
                              event={event}
                              cardMode="List"
                              withBookmarkData={result}
                              showFullTime={false}
                              deleteCallBack={() =>
                                String(query?.with_agenda) === 'true' &&
                                removeSession(sessionDay, session._id)
                              }
                              onClick={() => {
                                if (session.type === 'PRESENTATIONS') {
                                  logEvent('CLICKED_SESSION', {
                                    id: session._id,
                                    event_id: session.event,
                                    timeFilter,
                                    typeFilter,
                                    Search: query.q,
                                    Filters: `${timeFilter}, ${typeFilter}`,
                                  });
                                }
                                if (session.type === 'NETWORKING') {
                                  logEvent('CLICKED_NETWORKING_SESSION', {
                                    id: session._id,
                                    event_id: session.event,
                                    timeFilter,
                                    typeFilter,
                                    Search: query.q,
                                    Filters: `${timeFilter}, ${typeFilter}`,
                                  });
                                }
                              }}
                              embargoed={state?.embargoed}
                              passQueryParam
                            />
                          ))}
                      </CardGrid>
                    </div>
                  );
                })}

              {(data?.filter_total === 0 || Object.keys(data?.sessions || {}).length === 0) && (
                <EmptyState
                  icon={<BiCalendarX size="48px" color="lightGrey" />}
                  title={title}
                  subtitle={`Try with another combination ${
                    appliedFiltersCount ? `or clear filters` : ``
                  } to see the ${
                    String(query?.with_agenda) === 'true' ? 'Agenda' : 'full Program'
                  }`}
                >
                  {!!appliedFiltersCount && (
                    <Button onClick={() => clearFilters()}>Clear Filters</Button>
                  )}
                </EmptyState>
              )}

              {data && (
                <Paging
                  currentPage={data.page}
                  totalPages={data.total_pages}
                  pageRouteBuilder={pageRouteBuilder}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>
    </ContainerStyles.PageContainer>
  );
};

SessionsListContainer.disableEmbargoedPageBlock = true;
